<template>
    <div class="cs-code-editor" ref="container" :class="{ 'cs-dark': dark }">
        <div class="cs-editor-toolbar">
            <span class="cs-editor-lang">JavaScript</span>
            <button class="cs-editor-action" @click="formatCode" title="Format Code">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="21" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/>
                    <line x1="21" y1="14" x2="3" y2="14"/><line x1="21" y1="18" x2="3" y2="18"/>
                </svg>
            </button>
            <button class="cs-editor-action" @click="copyCode" title="Copy Code">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
            </button>
        </div>
        <div class="cs-editor-body" ref="editorBody"></div>
    </div>
</template>

<script>
import CodeMirror from 'codemirror';
import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/addon/hint/show-hint.js';
import 'codemirror/addon/hint/javascript-hint.js';
import 'codemirror/addon/edit/closebrackets.js';
import 'codemirror/addon/edit/matchbrackets.js';

// CandleScript-specific suggestions for the indicator builder
const CANDLE_SUGGESTIONS = [
    { text: 'sma', displayText: 'sma(source, length)', className: 'cs-hint-function', description: 'Simple Moving Average' },
    { text: 'ema', displayText: 'ema(source, length)', className: 'cs-hint-function', description: 'Exponential Moving Average' },
    { text: 'rsi', displayText: 'rsi(source, length)', className: 'cs-hint-function', description: 'Relative Strength Index' },
    { text: 'atr', displayText: 'atr(high, low, close, length)', className: 'cs-hint-function', description: 'Average True Range' },
    { text: 'highest', displayText: 'highest(arr, length)', className: 'cs-hint-function', description: 'Highest value in window' },
    { text: 'lowest', displayText: 'lowest(arr, length)', className: 'cs-hint-function', description: 'Lowest value in window' },
    { text: 'sum', displayText: 'sum(arr, length)', className: 'cs-hint-function', description: 'Rolling sum' },
    { text: 'stddev', displayText: 'stddev(arr, length)', className: 'cs-hint-function', description: 'Standard deviation' },
    { text: 'crossover', displayText: 'crossover(a, b)', className: 'cs-hint-function', description: 'A crosses above B' },
    { text: 'crossunder', displayText: 'crossunder(a, b)', className: 'cs-hint-function', description: 'A crosses below B' },
    { text: 'bbands', displayText: 'bbands(close, length, mult)', className: 'cs-hint-function', description: 'Bollinger Bands' },
    { text: 'macd', displayText: 'macd(close, fast, slow, signal)', className: 'cs-hint-function', description: 'MACD' },
    { text: 'wma', displayText: 'wma(source, length)', className: 'cs-hint-function', description: 'Weighted Moving Average' },
    { text: 'vwap', displayText: 'vwap(high, low, close, volume)', className: 'cs-hint-function', description: 'Volume Weighted Avg Price' },
    { text: 'stoch', displayText: 'stoch(high, low, close, kLen, dLen)', className: 'cs-hint-function', description: 'Stochastic Oscillator' },
    { text: 'input', displayText: 'input', className: 'cs-hint-property', description: 'OHLCV data object' },
    { text: 'options', displayText: 'options', className: 'cs-hint-property', description: 'User options object' },
    { text: 'open', displayText: 'open', className: 'cs-hint-property', description: 'Open prices array' },
    { text: 'high', displayText: 'high', className: 'cs-hint-property', description: 'High prices array' },
    { text: 'low', displayText: 'low', className: 'cs-hint-property', description: 'Low prices array' },
    { text: 'close', displayText: 'close', className: 'cs-hint-property', description: 'Close prices array' },
    { text: 'volume', displayText: 'volume', className: 'cs-hint-property', description: 'Volume array' },
    { text: 'Float64Array', displayText: 'Float64Array', className: 'cs-hint-class', description: 'Typed array constructor' },
    { text: 'Math.abs', displayText: 'Math.abs(x)', className: 'cs-hint-function', description: 'Absolute value' },
    { text: 'Math.max', displayText: 'Math.max(a, b)', className: 'cs-hint-function', description: 'Maximum value' },
    { text: 'Math.min', displayText: 'Math.min(a, b)', className: 'cs-hint-function', description: 'Minimum value' },
    { text: 'Math.sqrt', displayText: 'Math.sqrt(x)', className: 'cs-hint-function', description: 'Square root' },
    { text: 'Math.pow', displayText: 'Math.pow(base, exp)', className: 'cs-hint-function', description: 'Power' },
    { text: 'Math.round', displayText: 'Math.round(x)', className: 'cs-hint-function', description: 'Round to nearest integer' },
    { text: 'Math.floor', displayText: 'Math.floor(x)', className: 'cs-hint-function', description: 'Round down' },
    { text: 'Math.ceil', displayText: 'Math.ceil(x)', className: 'cs-hint-function', description: 'Round up' },
    { text: 'isNaN', displayText: 'isNaN(x)', className: 'cs-hint-function', description: 'Check if NaN' },
    { text: 'NaN', displayText: 'NaN', className: 'cs-hint-property', description: 'Not a Number constant' },
    { text: 'Infinity', displayText: 'Infinity', className: 'cs-hint-property', description: 'Infinity constant' },
    { text: 'function', displayText: 'function name() {}', className: 'cs-hint-keyword', description: 'Function declaration' },
    { text: 'const', displayText: 'const', className: 'cs-hint-keyword', description: 'Constant declaration' },
    { text: 'let', displayText: 'let', className: 'cs-hint-keyword', description: 'Variable declaration' },
    { text: 'return', displayText: 'return', className: 'cs-hint-keyword', description: 'Return statement' },
    { text: 'for', displayText: 'for (let i = 0; i < n; i++)', className: 'cs-hint-keyword', description: 'For loop' },
    { text: 'if', displayText: 'if (condition)', className: 'cs-hint-keyword', description: 'If statement' },
    // Drawing / Plotting API
    { text: 'plot.line', displayText: 'plot.line(values, opts)', className: 'cs-hint-function', description: 'Draw line series on chart' },
    { text: 'plot.hline', displayText: 'plot.hline(price, opts)', className: 'cs-hint-function', description: 'Horizontal line at price' },
    { text: 'plot.shape', displayText: 'plot.shape(series, opts)', className: 'cs-hint-function', description: 'Plot markers/shapes' },
    { text: 'plot.arrow', displayText: 'plot.arrow(series, opts)', className: 'cs-hint-function', description: 'Plot buy/sell arrows' },
    { text: 'plot.text', displayText: 'plot.text(bar, price, text, opts)', className: 'cs-hint-function', description: 'Plot text label' },
    { text: 'plot.textSeries', displayText: 'plot.textSeries(series, opts)', className: 'cs-hint-function', description: 'Plot text per bar' },
    { text: 'plot.candle', displayText: 'plot.candle({o,h,l,c}, opts)', className: 'cs-hint-function', description: 'Plot custom candles' },
    { text: 'plot.fill', displayText: 'plot.fill(s1, s2, opts)', className: 'cs-hint-function', description: 'Fill area between lines' },
    { text: 'plot.bgcolor', displayText: 'plot.bgcolor(colors)', className: 'cs-hint-function', description: 'Background color per bar' },
    { text: 'plot.histogram', displayText: 'plot.histogram(values, opts)', className: 'cs-hint-function', description: 'Plot histogram bars' },
];

/**
 * Custom hint function that combines CandleScript suggestions with JS hints
 */
function candleScriptHint(editor) {
    const cursor = editor.getCursor();
    const token = editor.getTokenAt(cursor);
    const line = editor.getLine(cursor.line);

    // Get the current word being typed
    let start = token.start;
    let end = cursor.ch;
    let word = token.string;

    // If token is a punctuation or operator, look back
    if (/[^\w.]/.test(word)) {
        word = '';
        start = end;
    }

    const prefix = word.toLowerCase();
    if (prefix.length < 1) return null;

    // Filter suggestions
    const matches = CANDLE_SUGGESTIONS.filter(s =>
        s.text.toLowerCase().startsWith(prefix) ||
        s.displayText.toLowerCase().includes(prefix)
    ).map(s => ({
        text: s.text,
        displayText: s.displayText,
        className: s.className,
        render: function(el, self, data) {
            const wrapper = document.createElement('div');
            wrapper.className = 'cs-hint-item';

            const icon = document.createElement('span');
            icon.className = 'cs-hint-icon ' + (s.className || '');
            if (s.className === 'cs-hint-function') icon.textContent = 'ƒ';
            else if (s.className === 'cs-hint-property') icon.textContent = '◆';
            else if (s.className === 'cs-hint-class') icon.textContent = '●';
            else icon.textContent = '⚡';
            wrapper.appendChild(icon);

            const name = document.createElement('span');
            name.className = 'cs-hint-name';
            name.textContent = s.displayText;
            wrapper.appendChild(name);

            const desc = document.createElement('span');
            desc.className = 'cs-hint-desc';
            desc.textContent = s.description;
            wrapper.appendChild(desc);

            el.appendChild(wrapper);
        },
    }));

    if (!matches.length) return null;

    return {
        list: matches.slice(0, 10),
        from: CodeMirror.Pos(cursor.line, start),
        to: CodeMirror.Pos(cursor.line, end),
    };
}

export default {
    name: 'CodeEditor',
    props: {
        code: { type: String, default: '' },
        errors: { type: Array, default: () => [] },
        dark: { type: Boolean, default: false },
    },
    data() {
        return {
            editor: null,
            ignoreChange: false,
        };
    },
    watch: {
        code(newVal) {
            if (this.editor && !this.ignoreChange) {
                const cursor = this.editor.getCursor();
                const scrollInfo = this.editor.getScrollInfo();
                this.editor.setValue(newVal || '');
                this.editor.setCursor(cursor);
                this.editor.scrollTo(scrollInfo.left, scrollInfo.top);
            }
        },
        dark(newVal) {
            if (this.editor) {
                this.editor.setOption('theme', newVal ? 'dracula' : 'default');
            }
        },
        errors() {
            this.updateErrorMarkers();
        },
    },
    mounted() {
        this.initEditor();
    },
    beforeDestroy() {
        if (this.editor) {
            // CodeMirror created via CodeMirror(element) doesn't have toTextArea.
            // Just remove the DOM element and null the reference.
            const wrapper = this.editor.getWrapperElement();
            if (wrapper && wrapper.parentNode) {
                wrapper.parentNode.removeChild(wrapper);
            }
            this.editor = null;
        }
    },
    methods: {
        initEditor() {
            const container = this.$refs.editorBody;
            if (!container) return;

            this.editor = CodeMirror(container, {
                value: this.code || '',
                mode: 'javascript',
                theme: this.dark ? 'dracula' : 'default',
                lineNumbers: true,
                lineWrapping: false,
                matchBrackets: true,
                autoCloseBrackets: true,
                indentUnit: 4,
                tabSize: 4,
                indentWithTabs: false,
                smartIndent: true,
                electricChars: true,
                extraKeys: {
                    'Ctrl-Space': 'autocomplete',
                    'Tab': (cm) => {
                        if (cm.somethingSelected()) {
                            cm.indentSelection('add');
                        } else {
                            cm.replaceSelection('    ', 'end');
                        }
                    },
                    'Shift-Tab': (cm) => {
                        cm.indentSelection('subtract');
                    },
                },
                hintOptions: {
                    hint: candleScriptHint,
                    completeSingle: false,
                    closeOnUnfocus: true,
                    alignWithWord: true,
                },
                gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers'],
            });

            // Listen for changes
            this.editor.on('change', (cm, change) => {
                if (change.origin === 'setValue') return;
                this.ignoreChange = true;
                this.$emit('change', cm.getValue());
                this.$nextTick(() => { this.ignoreChange = false; });
            });

            // Auto-show suggestions as user types
            this.editor.on('inputRead', (cm, change) => {
                if (change.origin === '+input') {
                    const cursor = cm.getCursor();
                    const token = cm.getTokenAt(cursor);
                    // Trigger autocomplete after 2+ chars of an identifier
                    if (token.string && token.string.length >= 2 && /^[\w.]/.test(token.string)) {
                        cm.showHint({ hint: candleScriptHint, completeSingle: false });
                    }
                }
            });

            // Set initial error markers
            this.updateErrorMarkers();

            // Focus the editor
            this.$nextTick(() => {
                if (this.editor) this.editor.refresh();
            });
        },

        updateErrorMarkers() {
            if (!this.editor) return;
            // Clear existing markers
            this.editor.getAllMarks().forEach(m => m.clear());

            // Add error markers
            if (this.errors && this.errors.length) {
                this.errors.forEach(err => {
                    if (err.line) {
                        const lineIdx = err.line - 1;
                        const lineContent = this.editor.getLine(lineIdx);
                        if (lineContent !== undefined) {
                            this.editor.markText(
                                { line: lineIdx, ch: 0 },
                                { line: lineIdx, ch: lineContent.length },
                                {
                                    className: 'cs-error-highlight',
                                    title: err.message,
                                }
                            );
                        }
                    }
                });
            }
        },

        formatCode() {
            if (!this.editor) return;
            let code = this.editor.getValue();
            const lines = code.split('\n');
            let indent = 0;
            const formatted = [];

            for (let line of lines) {
                line = line.trim();
                if (!line) { formatted.push(''); continue; }
                if (line.startsWith('}') || line.startsWith(']') || line.startsWith(')')) {
                    indent = Math.max(0, indent - 1);
                }
                formatted.push('    '.repeat(indent) + line);
                if (line.endsWith('{') || line.endsWith('[') || line.endsWith('(')) {
                    indent++;
                }
            }

            const result = formatted.join('\n');
            this.editor.setValue(result);
            this.$emit('change', result);
        },

        copyCode() {
            if (!this.editor) return;
            const text = this.editor.getValue();
            if (navigator.clipboard) {
                navigator.clipboard.writeText(text);
            }
        },

        /** Public method: focus the editor */
        focus() {
            if (this.editor) this.editor.focus();
        },

        /** Public method: get current code */
        getValue() {
            return this.editor ? this.editor.getValue() : this.code;
        },
    },
};
</script>

<style>
/* CodeMirror base styles */
@import '~codemirror/lib/codemirror.css';
@import '~codemirror/addon/hint/show-hint.css';
@import '~codemirror/theme/dracula.css';
</style>

<style scoped>
.cs-code-editor {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--tc-bg-primary, #fff);
    border-radius: 6px;
    overflow: hidden;
}

.cs-editor-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    border-bottom: 1px solid var(--tc-border-secondary, #f0f3fa);
    flex-shrink: 0;
    background: var(--tc-bg-secondary, #f8f9fd);
}

.cs-editor-lang {
    font-size: 11px;
    color: var(--tc-text-tertiary, #9ca3af);
    font-weight: 600;
    flex: 1;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.cs-editor-action {
    background: none;
    border: none;
    color: var(--tc-text-tertiary, #9ca3af);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
}
.cs-editor-action:hover {
    background: var(--tc-bg-hover, rgba(0,0,0,0.04));
    color: var(--tc-text-primary, #131722);
}

.cs-editor-body {
    flex: 1;
    overflow: hidden;
    position: relative;
}
</style>

<style>

.CodeMirror {
    height: 100% !important;
}
/* CodeMirror overrides for the indicator builder */
.cs-code-editor .CodeMirror {
    height: 100%;
    font-family: 'SF Mono', 'Fira Code', 'JetBrains Mono', 'Consolas', monospace;
    font-size: 13px;
    line-height: 1.6;
    padding: 4px 0;
}

.cs-code-editor .CodeMirror-gutters {
    border-right: 1px solid var(--tc-border-secondary, #f0f3fa);
    background: var(--tc-bg-secondary, #f8f9fd);
}

.cs-code-editor .CodeMirror-linenumber {
    font-size: 11px;
    color: var(--tc-text-tertiary, #9ca3af);
    padding: 0 8px 0 4px;
}

/* Light theme token colors */
.cs-code-editor .cm-keyword { color: #d73a49; font-weight: 500; }
.cs-code-editor .cm-def { color: #6f42c1; }
.cs-code-editor .cm-variable { color: #24292e; }
.cs-code-editor .cm-variable-2 { color: #005cc5; }
.cs-code-editor .cm-variable-3 { color: #6f42c1; }
.cs-code-editor .cm-property { color: #005cc5; }
.cs-code-editor .cm-operator { color: #d73a49; }
.cs-code-editor .cm-number { color: #005cc5; }
.cs-code-editor .cm-string { color: #032f62; }
.cs-code-editor .cm-string-2 { color: #032f62; }
.cs-code-editor .cm-comment { color: #6a737d; font-style: italic; }
.cs-code-editor .cm-atom { color: #005cc5; }
.cs-code-editor .cm-builtin { color: #6f42c1; }

/* Dark theme - dracula handles most, but override for consistency */
.cs-dark .cs-editor-toolbar {
    background: #282a36;
    border-color: #44475a;
}
.cs-dark .cs-editor-lang {
    color: #6272a4;
}
.cs-dark .cs-editor-action {
    color: #6272a4;
}
.cs-dark .cs-editor-action:hover {
    color: #f8f8f2;
    background: rgba(255,255,255,0.05);
}

/* Error highlight */
.cs-error-highlight {
    background: rgba(239, 68, 68, 0.15);
    border-bottom: 2px wavy #ef4444;
}

/* Autocomplete hint styles */
.CodeMirror-hints {
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 12px;
    border-radius: 8px;
    border: 1px solid var(--tc-border-primary, #e0e3eb);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    padding: 4px;
    max-height: 240px;
    z-index: 1000;
    background: #fff;
}

.cs-dark .CodeMirror-hints {
    background: #282a36;
    border-color: #44475a;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.CodeMirror-hint {
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    color: #24292e;
}

.cs-dark .CodeMirror-hint {
    color: #f8f8f2;
}

.CodeMirror-hint-active {
    background: rgba(41, 98, 255, 0.1);
    color: #2962ff;
}

.cs-dark .CodeMirror-hint-active {
    background: rgba(98, 114, 164, 0.3);
    color: #f8f8f2;
}

/* Custom hint item rendering */
.cs-hint-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
}

.cs-hint-icon {
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
}

.cs-hint-function .cs-hint-icon,
.cs-hint-icon.cs-hint-function {
    background: #dbeafe;
    color: #2563eb;
}
.cs-hint-property .cs-hint-icon,
.cs-hint-icon.cs-hint-property {
    background: #fef3c7;
    color: #d97706;
}
.cs-hint-class .cs-hint-icon,
.cs-hint-icon.cs-hint-class {
    background: #ede9fe;
    color: #7c3aed;
}
.cs-hint-keyword .cs-hint-icon,
.cs-hint-icon.cs-hint-keyword {
    background: #fce7f3;
    color: #db2777;
}

.cs-dark .cs-hint-icon.cs-hint-function { background: rgba(37,99,235,0.2); color: #60a5fa; }
.cs-dark .cs-hint-icon.cs-hint-property { background: rgba(217,119,6,0.2); color: #fbbf24; }
.cs-dark .cs-hint-icon.cs-hint-class { background: rgba(124,58,237,0.2); color: #a78bfa; }
.cs-dark .cs-hint-icon.cs-hint-keyword { background: rgba(219,39,119,0.2); color: #f472b6; }

.cs-hint-name {
    font-weight: 500;
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 12px;
}

.cs-hint-desc {
    font-size: 11px;
    color: #9ca3af;
    margin-left: auto;
    padding-left: 12px;
}

.cs-dark .cs-hint-desc {
    color: #6272a4;
}
</style>
